// Option-based field types shared by flow fields and client custom fields.
//
// Client custom fields (QL-123): options are rows in custom_field_option and
// values store the option's id instead of its label — dropdown/radio store a
// single option id, multiselect a JSON array of option ids. The same
// validateFieldValue applies: callers pass the field's option ids serialized
// via optionIdsJson() as the "options" list.
//
// Flow fields (QL-201): same id-based model with rows in flow_field_option —
// but a flow is bound to one language, so each option carries a single
// `label` string instead of labelI18n. The flow-specific resolvers below
// mirror the localized ones.

import { normalizeSyncKey } from "./financial-situation-fields";
import { getLocalizedValue, type LocaleCode } from "./i18n";
import type { CustomFieldOption } from "./types/custom-fields";

export const OPTION_FIELD_TYPES = ["dropdown", "radio", "multiselect"] as const;

export type OptionFieldType = (typeof OPTION_FIELD_TYPES)[number];

export function isOptionFieldType(type: string): boolean {
  return (OPTION_FIELD_TYPES as readonly string[]).includes(type);
}

// QL-237 — field types whose values have no order, so Greater than / Less
// than mean nothing on them. Number, date and datetime are the ordered types;
// an empty or unknown type is left out so a sentinel source keeps its own menu.
const UNORDERED_FIELD_TYPES = [
  "text",
  "textarea",
  "email",
  "phone",
  "dropdown",
  "radio",
  "boolean",
] as const;

export function isUnorderedFieldType(type: string): boolean {
  return (UNORDERED_FIELD_TYPES as readonly string[]).includes(type);
}

// QL-254 — a client custom field of any type can drive a condition rule,
// except "user": only the standard Client Owner field has it, and standard
// fields are not custom_field rows, so such a rule could never resolve.
export function isConditionSourceCustomFieldType(type: string): boolean {
  return type !== "user";
}

// QL-254 — client custom field types a condition can only test for presence
// (Is Empty / Is Not Empty): free text, dates and date-times on the client
// profile. The public flow receives only a presence marker for these (QL-211).
const PRESENCE_ONLY_CUSTOM_FIELD_TYPES = ["text", "textarea", "date", "datetime"] as const;

export function isPresenceOnlyCustomFieldType(type: string): boolean {
  return (PRESENCE_ONLY_CUSTOM_FIELD_TYPES as readonly string[]).includes(type);
}

/** Parse a field's options column (JSON array of strings) into a string array. */
export function parseFieldOptions(options: string | null | undefined): string[] {
  if (!options) return [];
  try {
    const parsed = JSON.parse(options);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/** The subset of an option row that label resolution needs. */
export type OptionLabelSource = Pick<CustomFieldOption, "id" | "labelI18n">;

/**
 * Serialize a field's option ids into the JSON-array-of-strings shape that
 * validateFieldValue expects as its `options` argument. Pass ALL options
 * (archived included) when validating saves so re-saving a client whose answer
 * points at a since-archived option still passes.
 */
export function optionIdsJson(options: Array<{ id: string }> | null | undefined): string {
  return JSON.stringify((options ?? []).map((o) => o.id));
}

/**
 * Resolve an option id to its localized label. Falls back to the raw id when
 * the id is unknown (legacy/unmigrated values must still render something).
 */
export function resolveOptionLabel(
  options: OptionLabelSource[] | null | undefined,
  optionId: string,
  locale: string,
  defaultLang?: string,
): string {
  const option = options?.find((o) => o.id === optionId);
  if (!option) return optionId;
  return (
    getLocalizedValue(
      option.labelI18n,
      locale as LocaleCode,
      defaultLang as LocaleCode | undefined,
    ) || optionId
  );
}

/**
 * Format a stored option-field value (option id, or JSON array of ids for
 * multiselect) as localized display text. Non-option field types return the
 * value unchanged.
 */
export function formatOptionFieldValue(
  fieldType: string | null | undefined,
  value: string,
  options: OptionLabelSource[] | null | undefined,
  locale: string,
  defaultLang?: string,
): string {
  if (!value || !fieldType || !isOptionFieldType(fieldType)) return value;
  if (fieldType === "multiselect") {
    return parseMultiselectValue(value)
      .map((id) => resolveOptionLabel(options, id, locale, defaultLang))
      .join(", ");
  }
  return resolveOptionLabel(options, value, locale, defaultLang);
}

/** The subset of a flow_field_option row that label resolution needs (QL-201). */
export type FlowOptionLabelSource = { id: string; label: string };

/**
 * Resolve a flow field option id to its label. Falls back to the raw id when
 * the id is unknown (legacy/unmigrated values must still render something).
 */
export function resolveFlowOptionLabel(
  options: FlowOptionLabelSource[] | null | undefined,
  optionId: string,
): string {
  const option = options?.find((o) => o.id === optionId);
  return option?.label || optionId;
}

/**
 * Format a stored flow option-field value (option id, or JSON array of ids
 * for multiselect) as display text. Non-option field types return the value
 * unchanged.
 */
export function formatFlowOptionFieldValue(
  fieldType: string | null | undefined,
  value: string,
  options: FlowOptionLabelSource[] | null | undefined,
): string {
  if (!value || !fieldType || !isOptionFieldType(fieldType)) return value;
  if (fieldType === "multiselect") {
    return parseMultiselectValue(value)
      .map((id) => resolveFlowOptionLabel(options, id))
      .join(", ");
  }
  return resolveFlowOptionLabel(options, value);
}

/**
 * A stored flow option-field value (option id, or JSON array of ids for
 * multiselect) as sync keys, in the same shape. Null when the value is empty
 * or any selected option has no sync key — a partial answer would read as a
 * different selection. An archived option reads as keyless: its key may
 * already belong to a new option, and writing it back would select that one.
 */
export function formatFlowOptionFieldSyncKey(
  fieldType: string,
  value: string | null,
  options: Array<{ id: string; isArchived?: boolean; syncKey?: string | null }> | null | undefined,
): string | null {
  if (!value || !isOptionFieldType(fieldType)) return null;
  const keyOf = (id: string) => {
    const option = options?.find((o) => o.id === id);
    return option && !option.isArchived ? (option.syncKey ?? null) : null;
  };
  if (fieldType === "multiselect") {
    const keys = parseMultiselectValue(value).map(keyOf);
    if (keys.length === 0 || keys.some((k) => k === null)) return null;
    return JSON.stringify(keys);
  }
  return keyOf(value);
}

/** Public API only: its validation issues are English by design. */
export type SyncKeyResolution = { ok: true; value: string } | { ok: false; error: string };

/**
 * Resolve a `valueSyncKey` write — option sync keys instead of a value — to
 * the stored value: the option id (multiselect: a JSON array of keys → a JSON
 * array of ids). Keys are trimmed like stored ones (`normalizeSyncKey`) and
 * matched case-sensitively. Only active options match; an archived option's
 * key may have been reused. "" clears the answer, like an empty `value`.
 */
export function resolveFlowOptionSyncKeyInput(
  fieldType: string,
  options: Array<{ id: string; isArchived?: boolean; syncKey?: string | null }>,
  input: string,
): SyncKeyResolution {
  if (!isOptionFieldType(fieldType)) {
    return {
      ok: false,
      error: "valueSyncKey is only accepted on option fields (dropdown, radio, multiselect)",
    };
  }
  if (input.trim() === "") return { ok: true, value: "" };

  const idOf = (raw: string) => {
    const key = normalizeSyncKey(raw);
    return key === undefined
      ? undefined
      : options.find((o) => !o.isArchived && o.syncKey != null && o.syncKey === key)?.id;
  };
  const unknown = (key: string): SyncKeyResolution => ({
    ok: false,
    error: `No option of this field has the sync key "${key}"`,
  });

  if (fieldType === "multiselect") {
    let parsed: unknown;
    try {
      parsed = JSON.parse(input);
    } catch {
      parsed = null;
    }
    if (!Array.isArray(parsed) || parsed.some((item) => typeof item !== "string")) {
      return { ok: false, error: "Multiselect valueSyncKey must be a JSON array of sync keys" };
    }
    const ids: string[] = [];
    for (const key of parsed as string[]) {
      const id = idOf(key);
      if (!id) return unknown(key);
      ids.push(id);
    }
    return { ok: true, value: JSON.stringify(ids) };
  }

  const id = idOf(input);
  return id ? { ok: true, value: id } : unknown(input);
}

/**
 * The options a flow field's input control should offer: active options in
 * order, plus any archived option the current answer still references (so an
 * old answer keeps rendering; it disappears once deselected).
 */
export function visibleFlowOptions<T extends { id: string; isArchived: boolean }>(
  options: T[] | null | undefined,
  selectedIds: Array<string | null | undefined>,
): T[] {
  return (options ?? []).filter((o) => !o.isArchived || selectedIds.includes(o.id));
}

/**
 * Resolve one submitted flow option value to its option id (QL-201): an
 * option id passes through; a label maps to its option's id (active options
 * win over archived ones on a label collision). Anything else is returned
 * unchanged so normal validation rejects it. Mirrors the client-field rule
 * (QL-123). Option sync keys are NOT matched here — they come in through their
 * own `valueSyncKey` input (resolveFlowOptionSyncKeyInput), so a key can never
 * shadow another option's label.
 */
export function resolveFlowOptionValue(
  options: Array<FlowOptionLabelSource & { isArchived?: boolean }>,
  value: string,
): string {
  if (options.some((o) => o.id === value)) return value;
  const byLabel = (candidates: typeof options) => candidates.find((o) => o.label === value);
  const match = byLabel(options.filter((o) => !o.isArchived)) ?? byLabel(options);
  return match ? match.id : value;
}

/**
 * Accept option ids or option labels for option-based flow fields: map each
 * submitted value to the option's id before validation, so the stored value
 * is the id the UI controls expect.
 */
export function canonicalizeFlowOptionInput(
  fieldType: string,
  options: Array<FlowOptionLabelSource & { isArchived?: boolean }>,
  value: string,
): string {
  if (!isOptionFieldType(fieldType)) return value;

  if (fieldType === "multiselect") {
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      return value;
    }
    if (!Array.isArray(parsed) || parsed.some((item) => typeof item !== "string")) return value;
    return JSON.stringify(
      (parsed as string[]).map((item) => resolveFlowOptionValue(options, item)),
    );
  }

  return resolveFlowOptionValue(options, value);
}

/**
 * Parse a multiselect stored value ('["A","B"]') into a string array.
 * Tolerates empty/plain-string values so scalar sources degrade gracefully.
 */
export function parseMultiselectValue(value: string | null | undefined): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * Membership test for the "contains" conditional operator. Multiselect source
 * values (JSON arrays) test membership; scalar sources degrade to equality.
 */
export function valueContains(fieldValue: string | null | undefined, expected: string): boolean {
  if (!fieldValue) return false;
  const items = parseMultiselectValue(fieldValue);
  if (items.length > 0) return items.includes(expected);
  return fieldValue === expected;
}

/**
 * Parse a stored value as a number, tolerating `,` as a decimal separator and
 * spaces as thousands separators (Norwegian locale). Returns null when the
 * value is not representable as a finite number — unlike parseFloat, which
 * silently truncates ("1,5" → 1, "0x1F" → 0).
 */
const NUMBER_PATTERN = /^([+-]?)(\d+(?:\.\d+)?|\.\d+)(?:[eE]([+-]?\d+))?$/;

function normalizeNumberInput(value: string): string {
  return value.trim().replace(/\s/g, "").replace(",", ".");
}

export function parseNumberLenient(value: string | null | undefined): number | null {
  if (!value?.trim()) return null;
  const normalized = normalizeNumberInput(value);
  if (!NUMBER_PATTERN.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Canonicalize an already-validated number value into a plain decimal string
 * without ever going through a double for the representation — String(Number)
 * would lose digits above 2^53 and rewrite values ≥1e21 into scientific
 * notation. Callers must have checked parseNumberLenient(value) !== null.
 */
function canonicalizeNumberString(value: string): string {
  const normalized = normalizeNumberInput(value);
  const [, sign, mantissa, exponent] = NUMBER_PATTERN.exec(normalized) ?? [];
  if (mantissa === undefined) return normalized;
  // "-0", "0.00" and underflow like "1e-400" all stored "0" before; keep that.
  if (Number(normalized) === 0) return "0";
  const dot = mantissa.indexOf(".");
  const digits = dot === -1 ? mantissa : mantissa.slice(0, dot) + mantissa.slice(dot + 1);
  // The decimal point sits this many digits from the left once the exponent is
  // folded in; parseNumberLenient's finite check bounds it, so the zero-padding
  // below stays small.
  const point = (dot === -1 ? mantissa.length : dot) + Number(exponent ?? 0);
  let intPart: string;
  let fracPart: string;
  if (point <= 0) {
    intPart = "0";
    fracPart = "0".repeat(-point) + digits;
  } else if (point >= digits.length) {
    intPart = digits + "0".repeat(point - digits.length);
    fracPart = "";
  } else {
    intPart = digits.slice(0, point);
    fracPart = digits.slice(point);
  }
  intPart = intPart.replace(/^0+(?=\d)/, "");
  fracPart = fracPart.replace(/0+$/, "");
  return (sign === "-" ? "-" : "") + intPart + (fracPart ? `.${fracPart}` : "");
}

/**
 * The canonical string a field's control actually renders as its answer, or
 * null when the control renders nothing (QPBI-395: a rule must never see a
 * value the control cannot show).
 *
 * boolean keeps BOTH explicit answers — the checkbox deliberately stores
 * "true"/"false" so rules can match either one; only garbage ("TRUE", "1",
 * "yes", …) and unset become null.
 */
export function normalizeVisibleValue(
  fieldType: string | null | undefined,
  value: string | null | undefined,
): string | null {
  const raw = value ?? "";
  switch (fieldType) {
    case "boolean":
      return raw === "true" || raw === "false" ? raw : null;
    case "multiselect":
      return parseMultiselectValue(raw).length > 0 ? raw : null;
    default:
      return raw.trim() === "" ? null : raw;
  }
}

// QL-211 — accepted inputs of a datetime field: a date and a time of day
// separated by "T" or a space, seconds optional, then an optional "Z" or
// ±HH:mm offset. No offset means UTC. Fractional seconds are not accepted.
// The regional date formatter reads years 0-99 as 1900-1999, so a datetime
// before year 1000 could be stored but not rendered (PDF); refuse it.
const MIN_DATETIME_YEAR = 1000;

const DATETIME_INPUT =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:\d{2})?$/;

/**
 * QL-211 — the canonical stored form of a datetime value, UTC
 * "YYYY-MM-DDTHH:mm:ssZ", or null when the input is not a real date and time
 * of day. A date alone is rejected (no implicit midnight), as are calendar
 * rollovers (2026-02-31), out-of-range times (25:00, 12:60) and years outside
 * 1000-9999.
 *
 * The canonical form has a fixed width, so comparing two canonical strings
 * orders them chronologically.
 */
export function canonicalizeDateTime(value: string): string | null {
  const match = DATETIME_INPUT.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = match[6] === undefined ? 0 : Number(match[6]);
  if (year < MIN_DATETIME_YEAR || hour > 23 || minute > 59 || second > 59) return null;

  let offsetMinutes = 0;
  const zone = match[7];
  if (zone !== undefined && zone !== "Z") {
    const offsetHours = Number(zone.slice(1, 3));
    const offsetRest = Number(zone.slice(4, 6));
    if (offsetHours > 23 || offsetRest > 59) return null;
    offsetMinutes = (zone[0] === "-" ? -1 : 1) * (offsetHours * 60 + offsetRest);
  }

  // setUTCFullYear, not Date.UTC: Date.UTC maps years 0-99 to 1900-1999.
  const wallClock = new Date(0);
  wallClock.setUTCFullYear(year, month - 1, day);
  // Reject rollovers like 2026-02-31 → Mar 3.
  if (wallClock.getUTCMonth() !== month - 1 || wallClock.getUTCDate() !== day) return null;
  wallClock.setUTCHours(hour, minute, second, 0);

  const iso = new Date(wallClock.getTime() - offsetMinutes * 60_000).toISOString();
  // An offset can push the UTC year out of range (1000-01-01T00:00+01:00 is
  // 0999, 9999-12-31T23:00-02:00 is 10000).
  if (!/^\d{4}-/.test(iso) || Number(iso.slice(0, 4)) < MIN_DATETIME_YEAR) return null;
  return `${iso.slice(0, 19)}Z`;
}

/**
 * Why a value was refused, as data rather than a sentence, so each caller can
 * word it in its own language. Option lists are the values the field accepts
 * (option ids for option fields).
 */
export type FieldValueInvalidReason =
  | { kind: "boolean" }
  | { kind: "option"; allowed: string[] }
  | { kind: "multiselect_format" }
  | { kind: "multiselect_unknown"; unknown: string[]; allowed: string[] }
  | { kind: "number" }
  | { kind: "date" }
  | { kind: "datetime" };

export type FieldValueValidation =
  | { ok: true; value: string }
  | { ok: false; reason: FieldValueInvalidReason };

/**
 * Validate (and canonicalize) a value being SAVED into a field of the given
 * type (QPBI-395 fix #2). Single source of truth for every write path — flow
 * instance field values and client custom values alike.
 *
 * Contract:
 * - "" always passes for every type — it clears the answer; required-ness is
 *   the completion engine's job, not the save path's.
 * - An unrecognized fieldType passes the value through untouched — the type
 *   column is a plain varchar with no CHECK, so legacy rows must keep working.
 * - On ok:true the returned `value` is the canonical form to persist: numbers
 *   get "," → "." and thousands-spaces stripped, multiselect is re-serialized
 *   deduped in option order ("[]" collapses to ""), datetime is converted to
 *   UTC "YYYY-MM-DDTHH:mm:ssZ".
 */
export function validateFieldValue(
  fieldType: string | null | undefined,
  options: string | null | undefined,
  value: string,
): FieldValueValidation {
  if (value === "") return { ok: true, value };

  switch (fieldType) {
    case "boolean":
      return value === "true" || value === "false"
        ? { ok: true, value }
        : { ok: false, reason: { kind: "boolean" } };

    case "dropdown":
    case "radio": {
      const known = parseFieldOptions(options);
      return known.includes(value)
        ? { ok: true, value }
        : { ok: false, reason: { kind: "option", allowed: known } };
    }

    case "multiselect": {
      let parsed: unknown;
      try {
        parsed = JSON.parse(value);
      } catch {
        return { ok: false, reason: { kind: "multiselect_format" } };
      }
      if (!Array.isArray(parsed) || parsed.some((item) => typeof item !== "string")) {
        return { ok: false, reason: { kind: "multiselect_format" } };
      }
      const known = parseFieldOptions(options);
      const unknown = parsed.filter((item) => !known.includes(item));
      if (unknown.length > 0) {
        return { ok: false, reason: { kind: "multiselect_unknown", unknown, allowed: known } };
      }
      // Canonical form: unique selections in option order, "" when empty —
      // exactly what the checkbox-list control writes.
      const selected = known.filter((option) => parsed.includes(option));
      return { ok: true, value: selected.length > 0 ? JSON.stringify(selected) : "" };
    }

    case "number": {
      const n = parseNumberLenient(value);
      return n !== null
        ? { ok: true, value: canonicalizeNumberString(value) }
        : { ok: false, reason: { kind: "number" } };
    }

    case "date": {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      const parsed = match ? new Date(`${value}T00:00:00Z`) : null;
      const valid =
        match !== null &&
        parsed !== null &&
        !Number.isNaN(parsed.getTime()) &&
        // Reject rollovers like 2026-02-31 → Mar 3.
        parsed.getUTCDate() === Number(match[3]);
      return valid ? { ok: true, value } : { ok: false, reason: { kind: "date" } };
    }

    case "datetime": {
      const canonical = canonicalizeDateTime(value);
      return canonical !== null
        ? { ok: true, value: canonical }
        : { ok: false, reason: { kind: "datetime" } };
    }

    // text / textarea / email / phone (and unknown legacy types): free text.
    default:
      return { ok: true, value };
  }
}
